import { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Flex, Box } from 'theme-ui';

import { CustomizerModal } from '@snippets';

import { useSelectedOptions } from '@hooks';
import { Header } from '../Header';
import { Icons } from './Icons';
import { Options } from './Options';
import { SizeOptions } from './SizeOptions';
import { ColorOptions } from './ColorOptions';
import { AddToCart } from './AddToCart';
import { Personalize } from './Personalize';
import { Metadata } from './Metadata';
import { themed } from './Details.theme';
import { Upsells } from './Upsells';
import { BuyWithPrime } from '../BuyWithPrime';
import { ApplePay } from './ApplePay';

export const Details = themed(
  ({
    theme,
    product,
    selectedVariant,
    currentProduct,
    titles,
    productUpsells,
    rebuyProducts,
    videoWise,
    setIsAddToCartVisible,
    setSyncQuantity,
    syncQuantity,
  }) => {
    const [selectedOptions] = useSelectedOptions();
    const [isOpen, setIsOpen] = useState(false);
    const currentProductRef = useRef(null);

    const personalizedEnabled = useMemo(
      () => currentProduct?.tags?.includes('personalized-enabled'),
      [currentProduct?.id]
    );

    useEffect(() => {
      if (currentProduct && personalizedEnabled) {
        if (!currentProductRef?.current) {
          currentProductRef.current = currentProduct;
        } else if (
          currentProductRef.current?.handle !== currentProduct?.handle
        ) {
          window.location.reload();
        }
      }
    }, [
      currentProduct?.handle,
      currentProductRef?.current,
      personalizedEnabled,
    ]);

    return (
      <Container data-comp={Details.displayName} sx={theme.wrapper}>
        <Box data-comp="StickyContainer" sx={theme.sticky}>
          <Flex sx={theme.sticky.wrapper}>
            <Box sx={{ display: ['none', null, 'block'] }}>
              <Header
                selectedVariant={selectedVariant}
                currentProduct={currentProduct}
                titles={titles}
              />
            </Box>

            <Flex sx={theme.ctas}>
              {/* Sizes etc.. */}
              {product?.optionsMap && selectedVariant ? (
                <Options
                  options={product?.optionsMap}
                  handle={selectedVariant?.product?.handle}
                  image={selectedVariant?.image?.originalSrc}
                />
              ) : null}

              <SizeOptions product={currentProduct} />

              <ColorOptions product={product} />

              <AddToCart
                product={currentProduct}
                selectedVariant={selectedVariant}
                rebuyProducts={rebuyProducts}
                setIsAddToCartVisible={setIsAddToCartVisible}
                setSyncQuantity={setSyncQuantity}
                syncQuantity={syncQuantity}
              />

              <Personalize product={currentProduct} setIsOpen={setIsOpen} />

              {selectedVariant && (
                <ApplePay
                  selectedVariant={selectedVariant}
                  quantity={syncQuantity || 1}
                  product={currentProduct}
                />
              )}

              <BuyWithPrime sku={selectedVariant?.sku} />

              {!!videoWise?.length && <div className="w-full">{videoWise}</div>}

              {productUpsells?.length ? (
                <Box sx={{ width: '100%' }}>{productUpsells[0]}</Box>
              ) : (
                <Upsells selectedVariant={selectedVariant} />
              )}

              <Icons product={currentProduct} />

              {currentProduct && (
                <Metadata
                  product={currentProduct}
                  selectedVariant={selectedVariant}
                />
              )}
            </Flex>
          </Flex>
        </Box>
        <CustomizerModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          variantId={selectedVariant?.legacyResourceId}
          selectedColor={selectedOptions?.color || ''}
          productHandle={product?.handle}
          settings={{
            brand_logo: product?.images?.[0]?.url,
            show_saved_items: true,
            show_share_button: true,
          }}
        />
      </Container>
    );
  }
);

Details.displayName = 'ProductDetails';